import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

/* Procedural low-poly American open house — now interactive: every
   actor and prop has a click action (raycast), with speech bubbles,
   particle bursts, a working front door, mailbox mail, wind gusts. */

const C = {
  siding: 0xf3efe4,
  trim: 0xffffff,
  roof: 0x3d4643,
  door: 0x17503a,
  shutter: 0x1d5a40,
  window: 0xd9a940,
  brick: 0x9c4a35,
  grass: 0x2e8b62,
  grassDark: 0x1d6a48,
  soil: 0x5b4632,
  trunk: 0x6b4f36,
  gold: 0xd9a940,
  fence: 0xfaf7ef,
  path: 0xd6cfbe
};

function flat(color, extra) {
  return new THREE.MeshStandardMaterial(Object.assign({ color, flatShading: true, roughness: 0.85, metalness: 0.05 }, extra));
}

function smooth(color, extra) {
  return new THREE.MeshStandardMaterial(Object.assign({ color, roughness: 0.8, metalness: 0.05 }, extra));
}

/* ── sprite textures (cached) ───────────────────────────── */

const texCache = {};
function canvasTexture(key, w, h, draw) {
  if (texCache[key]) return texCache[key];
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  draw(cv.getContext('2d'), w, h);
  const tex = new THREE.CanvasTexture(cv);
  texCache[key] = tex;
  return tex;
}

function heartTex() {
  return canvasTexture('heart', 64, 64, (x, w, h) => {
    x.fillStyle = '#c65840';
    x.font = '52px Georgia';
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillText('♥', 32, 36);
  });
}

function dollarTex() {
  return canvasTexture('dollar', 64, 64, (x) => {
    x.fillStyle = '#d9a940';
    x.font = 'bold 50px Georgia';
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillText('$', 32, 34);
  });
}

function smokeTex() {
  return canvasTexture('smoke', 64, 64, (x) => {
    const g = x.createRadialGradient(32, 32, 4, 32, 32, 30);
    g.addColorStop(0, 'rgba(240,238,230,0.9)');
    g.addColorStop(1, 'rgba(240,238,230,0)');
    x.fillStyle = g;
    x.fillRect(0, 0, 64, 64);
  });
}

function leafTex() {
  return canvasTexture('leaf', 64, 64, (x) => {
    x.fillStyle = '#1d6a48';
    x.beginPath();
    x.ellipse(32, 32, 22, 11, 0.7, 0, Math.PI * 2);
    x.fill();
  });
}

function bubbleTex(text) {
  return canvasTexture('bubble:' + text, 760, 300, (x, w) => {
    const boxH = 220, tailH = 56, r = 38;

    // wrap into up to two lines at the largest size that fits
    let fontSize = 64;
    let lines = [text];
    const fits = (str, size) => { x.font = '700 ' + size + 'px Georgia, serif'; return x.measureText(str).width <= w - 70; };
    if (!fits(text, fontSize)) {
      const words = text.split(' ');
      let best = null;
      for (let split = 1; split < words.length; split++) {
        const l1 = words.slice(0, split).join(' ');
        const l2 = words.slice(split).join(' ');
        if (Math.abs(l1.length - l2.length) < (best ? Math.abs(best[0].length - best[1].length) : 1e9)) best = [l1, l2];
      }
      lines = best || [text];
      while (fontSize > 30 && !(fits(lines[0], fontSize) && fits(lines[1] || '', fontSize))) fontSize -= 4;
    }

    x.fillStyle = 'rgba(255,253,249,0.98)';
    x.strokeStyle = '#17503a';
    x.lineWidth = 9;
    x.beginPath();
    x.moveTo(r, 6);
    x.lineTo(w - r, 6);
    x.quadraticCurveTo(w - 6, 6, w - 6, r);
    x.lineTo(w - 6, boxH - r);
    x.quadraticCurveTo(w - 6, boxH, w - r, boxH);
    x.lineTo(w / 2 + 40, boxH);
    x.lineTo(w / 2, boxH + tailH);
    x.lineTo(w / 2 - 40, boxH);
    x.lineTo(r, boxH);
    x.quadraticCurveTo(6, boxH, 6, boxH - r);
    x.lineTo(6, r);
    x.quadraticCurveTo(6, 6, r, 6);
    x.closePath();
    x.fill();
    x.stroke();

    x.fillStyle = '#14201b';
    x.font = '700 ' + fontSize + 'px Georgia, serif';
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    if (lines.length === 1) {
      x.fillText(lines[0], w / 2, boxH / 2 + 4);
    } else {
      x.fillText(lines[0], w / 2, boxH / 2 - fontSize * 0.58 + 4);
      x.fillText(lines[1], w / 2, boxH / 2 + fontSize * 0.58 + 4);
    }
  });
}

/* ── people ─────────────────────────────────────────────── */

function makePerson(opts) {
  const g = new THREE.Group();
  const skin = smooth(opts.skin || 0xe8b98a);
  const jacket = smooth(opts.jacket || opts.top);
  const shirt = smooth(opts.shirt || opts.top);
  const pants = smooth(opts.pants);
  const shoes = smooth(opts.shoes || 0x2a2420);
  const cast = m => { m.castShadow = true; return m; };

  [-1, 1].forEach(side => {
    const leg = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.36, 8), pants));
    leg.position.set(0.068 * side, 0.2, 0);
    const shoe = cast(new THREE.Mesh(new THREE.BoxGeometry(0.095, 0.05, 0.16), shoes));
    shoe.position.set(0.068 * side, 0.025, 0.025);
    g.add(leg, shoe);
  });

  const hips = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.115, 0.1, 0.14, 10), pants));
  hips.position.y = 0.43;
  g.add(hips);

  const torso = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.125, 0.115, 0.36, 10), jacket));
  torso.position.y = 0.67;
  g.add(torso);

  const front = new THREE.Mesh(new THREE.BoxGeometry(0.085, 0.26, 0.02), shirt);
  front.position.set(0, 0.7, 0.115);
  g.add(front);
  [-1, 1].forEach(side => {
    const lapel = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.2, 0.022), jacket);
    lapel.position.set(0.058 * side, 0.72, 0.118);
    lapel.rotation.z = -0.22 * side;
    g.add(lapel);
  });

  const shoulders = cast(new THREE.Mesh(new THREE.SphereGeometry(0.128, 12, 10), jacket));
  shoulders.position.y = 0.85;
  shoulders.scale.y = 0.75;
  g.add(shoulders);
  const collar = new THREE.Mesh(new THREE.TorusGeometry(0.062, 0.02, 8, 14), shirt);
  collar.position.y = 0.9;
  collar.rotation.x = Math.PI / 2;
  g.add(collar);

  function arm(side) {
    const pivot = new THREE.Group();
    pivot.position.set(0.145 * side, 0.84, 0);
    const sleeve = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.045, 0.24, 8), jacket));
    sleeve.position.y = -0.12;
    const fore = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.038, 0.15, 8), jacket));
    fore.position.y = -0.3;
    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.042, 8, 8), skin);
    hand.position.y = -0.39;
    pivot.add(sleeve, fore, hand);
    g.add(pivot);
    return pivot;
  }
  const armL = arm(-1);
  const armR = arm(1);

  const headPivot = new THREE.Group();
  headPivot.position.y = 0.98;
  const head = cast(new THREE.Mesh(new THREE.SphereGeometry(0.115, 14, 12), skin));
  head.position.y = 0.09;
  headPivot.add(head);
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.022, 8, 8), skin);
  nose.position.set(0, 0.075, 0.112);
  headPivot.add(nose);
  [-1, 1].forEach(side => {
    const ear = new THREE.Mesh(new THREE.SphereGeometry(0.024, 8, 8), skin);
    ear.position.set(0.112 * side, 0.09, 0);
    headPivot.add(ear);
  });

  const hairMat = smooth(opts.hair);
  if (opts.hairstyle === 'bun') {
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.119, 14, 12, 0, Math.PI * 2, 0, Math.PI * 0.52), hairMat);
    cap.position.y = 0.1;
    const bun = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 10), hairMat);
    bun.position.set(0, 0.19, -0.085);
    headPivot.add(cap, bun);
  } else if (opts.hairstyle === 'side') {
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.12, 14, 12, 0, Math.PI * 2, 0, Math.PI * 0.5), hairMat);
    cap.position.y = 0.1;
    cap.rotation.z = 0.14;
    headPivot.add(cap);
  } else {
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.119, 14, 12, 0, Math.PI * 2, 0, Math.PI * 0.48), hairMat);
    cap.position.y = 0.105;
    headPivot.add(cap);
  }
  g.add(headPivot);

  return { group: g, armL, armR, head: headPivot };
}

/* ── house ──────────────────────────────────────────────── */

function buildHouse() {
  const house = new THREE.Group();
  const cast = m => { m.castShadow = true; m.receiveShadow = true; return m; };

  const W = 2.6, D = 1.8, H = 1.35;

  const body = cast(new THREE.Mesh(new THREE.BoxGeometry(W, H, D), flat(C.siding)));
  body.position.y = H / 2;
  house.add(body);

  const tri = new THREE.Shape();
  tri.moveTo(-W / 2 - 0.22, 0);
  tri.lineTo(W / 2 + 0.22, 0);
  tri.lineTo(0, 0.85);
  tri.lineTo(-W / 2 - 0.22, 0);
  const roof = cast(new THREE.Mesh(new THREE.ExtrudeGeometry(tri, { depth: D + 0.36, bevelEnabled: false }), flat(C.roof)));
  roof.position.set(0, H, -(D + 0.36) / 2);
  house.add(roof);

  const gable = new THREE.Shape();
  gable.moveTo(-W / 2, 0); gable.lineTo(W / 2, 0); gable.lineTo(0, 0.8); gable.lineTo(-W / 2, 0);
  [1, -1].forEach(side => {
    const end = new THREE.Mesh(new THREE.ExtrudeGeometry(gable, { depth: 0.03, bevelEnabled: false }), flat(C.siding));
    end.position.set(0, H, side * (D / 2 - 0.015) - 0.015);
    house.add(end);
  });

  const chimney = cast(new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.0, 0.3), flat(C.brick)));
  chimney.position.set(0.85, H + 0.55, -0.35);
  house.add(chimney);
  const capMesh = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.07, 0.36), flat(0xd6cfbe));
  capMesh.position.set(0.85, H + 1.07, -0.35);
  house.add(capMesh);

  const porchD = 0.62;
  const slab = cast(new THREE.Mesh(new THREE.BoxGeometry(W * 0.72, 0.1, porchD), flat(0xe3ddcf)));
  slab.position.set(0, 0.05, D / 2 + porchD / 2);
  house.add(slab);
  const step = cast(new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.06, 0.22), flat(0xd6cfbe)));
  step.position.set(0, 0.03, D / 2 + porchD + 0.1);
  house.add(step);

  const porchRoof = cast(new THREE.Mesh(new THREE.BoxGeometry(W * 0.78, 0.07, porchD + 0.22), flat(C.roof)));
  porchRoof.position.set(0, H - 0.08, D / 2 + porchD / 2 + 0.03);
  porchRoof.rotation.x = 0.1;
  house.add(porchRoof);

  [-1, 1].forEach(x => {
    const col = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.05, H - 0.2, 8), flat(C.trim, { flatShading: false })));
    col.position.set(x * (W * 0.34), (H - 0.2) / 2 + 0.1, D / 2 + porchD - 0.1);
    house.add(col);
  });

  // door on a hinge pivot so it can swing open
  const doorPivot = new THREE.Group();
  doorPivot.position.set(-0.23, 0.5, D / 2 + 0.03);
  const doorMesh = cast(new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.8, 0.05), flat(C.door)));
  doorMesh.position.x = 0.23;
  doorPivot.add(doorMesh);
  const knob = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 8), smooth(C.gold, { metalness: 0.9, roughness: 0.3 }));
  knob.position.set(0.38, 0, 0.04);
  doorPivot.add(knob);
  house.add(doorPivot);

  // dark doorway behind the door + inviting inner glow
  const doorway = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.8, 0.02), new THREE.MeshStandardMaterial({ color: 0x2a1f14, emissive: 0xd9a940, emissiveIntensity: 0.12 }));
  doorway.position.set(0, 0.5, D / 2 - 0.02);
  house.add(doorway);

  const transom = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.12, 0.04), smooth(C.window, { emissive: C.window, emissiveIntensity: 0.7 }));
  transom.position.set(0, 0.98, D / 2 + 0.03);
  house.add(transom);

  const winMat = new THREE.MeshStandardMaterial({ color: C.window, emissive: C.window, emissiveIntensity: 0.85, roughness: 0.4 });
  function window2(x, y, z, ry) {
    const wg = new THREE.Group();
    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.56, 0.04), flat(C.trim, { flatShading: false }));
    const glass = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.46, 0.05), winMat);
    const barH = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.025, 0.055), flat(C.trim, { flatShading: false }));
    const barV = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.46, 0.055), flat(C.trim, { flatShading: false }));
    wg.add(frame, glass, barH, barV);
    [-1, 1].forEach(s => {
      const shutter = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.56, 0.03), flat(C.shutter));
      shutter.position.x = 0.3 * s;
      wg.add(shutter);
    });
    wg.position.set(x, y, z);
    wg.rotation.y = ry || 0;
    house.add(wg);
  }
  window2(-0.82, 0.72, D / 2 + 0.02, 0);
  window2(0.82, 0.72, D / 2 + 0.02, 0);
  window2(0, 0.72, -D / 2 - 0.02, Math.PI);
  window2(-0.7, 0.72, -D / 2 - 0.02, Math.PI);

  const glow = new THREE.PointLight(0xd9a940, 0.65, 4);
  glow.position.set(0, 0.9, 0);
  house.add(glow);

  return { group: house, doorPivot, glow, winMat, chimneyLocal: new THREE.Vector3(0.85, H + 1.15, -0.35) };
}

/* ── flag ───────────────────────────────────────────────── */

function makeFlag() {
  const g = new THREE.Group();
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 1.1, 8), smooth(0xd6cfbe, { metalness: 0.6, roughness: 0.4 }));
  pole.position.y = 0.55;
  g.add(pole);
  const ball = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 8), smooth(C.gold, { metalness: 0.9, roughness: 0.3 }));
  ball.position.y = 1.12;
  g.add(ball);

  const tex = canvasTexture('usflag', 128, 80, (x) => {
    for (let i = 0; i < 13; i++) {
      x.fillStyle = i % 2 === 0 ? '#B22234' : '#ffffff';
      x.fillRect(0, i * (80 / 13), 128, 80 / 13 + 1);
    }
    x.fillStyle = '#3C3B6E';
    x.fillRect(0, 0, 52, 43);
    x.fillStyle = '#ffffff';
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 6; c++) {
        x.beginPath();
        x.arc(6 + c * 8, 5 + r * 8, 1.6, 0, Math.PI * 2);
        x.fill();
      }
    }
  });

  const flagGeo = new THREE.PlaneGeometry(0.52, 0.32, 12, 6);
  const flag = new THREE.Mesh(flagGeo, new THREE.MeshStandardMaterial({ map: tex, side: THREE.DoubleSide, roughness: 0.85 }));
  flag.position.set(0.27, 0.93, 0);
  g.add(flag);

  return { group: g, flag, base: flagGeo.attributes.position.array.slice() };
}

/* ── scene ──────────────────────────────────────────────── */

function buildScene() {
  const scene = new THREE.Scene();

  scene.add(new THREE.HemisphereLight(0xfff6e0, 0x0a2c1f, 0.85));
  const sun = new THREE.DirectionalLight(0xffeecb, 1.15);
  sun.position.set(5, 8, 4);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.left = -6; sun.shadow.camera.right = 6;
  sun.shadow.camera.top = 6; sun.shadow.camera.bottom = -6;
  scene.add(sun);
  const rim = new THREE.DirectionalLight(0x8fd4b4, 0.35);
  rim.position.set(-6, 3, -5);
  scene.add(rim);

  const island = new THREE.Group();
  scene.add(island);
  const cast = m => { m.castShadow = true; m.receiveShadow = true; return m; };

  const grass = cast(new THREE.Mesh(new THREE.CylinderGeometry(3.4, 3.6, 0.55, 12), flat(C.grass)));
  grass.position.y = -0.28;
  island.add(grass);
  const soil = cast(new THREE.Mesh(new THREE.CylinderGeometry(3.6, 1.9, 1.6, 12), flat(C.soil)));
  soil.position.y = -1.35;
  island.add(soil);
  const soilTip = cast(new THREE.Mesh(new THREE.CylinderGeometry(1.9, 0.2, 1.2, 12), flat(0x4a3826)));
  soilTip.position.y = -2.75;
  island.add(soilTip);

  const houseObj = buildHouse();
  houseObj.group.position.set(-0.3, 0, -0.7);
  houseObj.group.userData.hit = 'house';
  island.add(houseObj.group);

  [[0, 0.62], [0, 1.02], [0, 1.42], [0, 1.82]].forEach(p => {
    const pad = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.045, 0.34), flat(C.path));
    pad.position.set(-0.3 + p[0], 0.02, p[1]);
    pad.receiveShadow = true;
    island.add(pad);
  });

  const fenceMat = flat(C.fence, { flatShading: false });
  function picketRun(x0, x1, z) {
    const run = new THREE.Group();
    const n = Math.max(2, Math.round(Math.abs(x1 - x0) / 0.17));
    for (let i = 0; i <= n; i++) {
      const x = x0 + (x1 - x0) * (i / n);
      const picket = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.34, 0.02), fenceMat);
      picket.position.set(x, 0.17, z);
      picket.castShadow = true;
      run.add(picket);
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.038, 0.06, 4), fenceMat);
      tip.rotation.y = Math.PI / 4;
      tip.position.set(x, 0.37, z);
      run.add(tip);
    }
    [0.1, 0.26].forEach(y => {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(Math.abs(x1 - x0) + 0.06, 0.045, 0.018), fenceMat);
      rail.position.set((x0 + x1) / 2, y, z + 0.012);
      run.add(rail);
    });
    island.add(run);
  }
  picketRun(-2.35, -0.75, 2.05);
  picketRun(0.15, 1.9, 2.05);

  const mailbox = new THREE.Group();
  mailbox.userData.hit = 'mailbox';
  const mbPost = cast(new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.62, 0.05), flat(C.trunk)));
  mbPost.position.y = 0.31;
  mailbox.add(mbPost);
  const mbBody = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.3, 12, 1, false, 0, Math.PI), smooth(0x3d4643)));
  mbBody.rotation.z = Math.PI / 2;
  mbBody.position.y = 0.68;
  mailbox.add(mbBody);
  const mbFloor = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.02, 0.18), smooth(0x3d4643));
  mbFloor.position.y = 0.6;
  mailbox.add(mbFloor);
  const mbFlagPivot = new THREE.Group();
  mbFlagPivot.position.set(0.1, 0.68, 0.1);
  const mbFlag = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.12, 0.03), smooth(0xc0392b));
  mbFlag.position.y = 0.06;
  mbFlagPivot.add(mbFlag);
  mailbox.add(mbFlagPivot);
  const envelope = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.005, 0.11), smooth(0xfffdf9));
  envelope.position.y = 0.62;
  envelope.visible = false;
  mailbox.add(envelope);
  mailbox.position.set(0.42, 0, 2.25);
  mailbox.rotation.y = -0.2;
  island.add(mailbox);

  const oak = new THREE.Group();
  oak.userData.hit = 'oak';
  const oakTrunk = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 0.7, 7), flat(C.trunk)));
  oakTrunk.position.y = 0.35;
  oak.add(oakTrunk);
  const canopy = new THREE.Group();
  [[0, 0.95, 0, 0.52], [0.3, 0.8, 0.1, 0.34], [-0.28, 0.82, -0.08, 0.3]].forEach(s => {
    const puff = cast(new THREE.Mesh(new THREE.SphereGeometry(s[3], 9, 8), flat(C.grassDark)));
    puff.position.set(s[0], s[1], s[2]);
    canopy.add(puff);
  });
  oak.add(canopy);
  oak.position.set(2.1, 0, 0.2);
  island.add(oak);

  const pineT = new THREE.Group();
  const pTrunk = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 0.35, 6), flat(C.trunk)));
  pTrunk.position.y = 0.17;
  pineT.add(pTrunk);
  [0.55, 0.38, 0.24].forEach((r, i) => {
    const cone = cast(new THREE.Mesh(new THREE.ConeGeometry(r, 0.55, 7), flat(i === 1 ? C.grassDark : 0x1d6a48)));
    cone.position.y = 0.45 + i * 0.38;
    pineT.add(cone);
  });
  pineT.position.set(-2.3, 0, -0.6);
  island.add(pineT);

  [[-1.15, 0.35], [0.62, 0.35], [1.05, 0.15]].forEach(p => {
    const bush = cast(new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 7), flat(C.grassDark)));
    bush.position.set(-0.3 + p[0], 0.12, 0.4 + p[1]);
    bush.scale.y = 0.8;
    island.add(bush);
  });

  const flagObj = makeFlag();
  flagObj.group.position.set(0.72, 0.1, 0.28);
  flagObj.group.rotation.z = -0.16;
  flagObj.group.userData.hit = 'flag';
  island.add(flagObj.group);

  // people
  const agent = makePerson({
    jacket: 0x24354d, shirt: 0xffffff, pants: 0x1b2521,
    hair: 0x2e2019, hairstyle: 'side', skin: 0xe8b98a, shoes: 0x2a2420
  });
  agent.group.position.set(0.55, 0, 1.15);
  agent.group.rotation.y = -1.05;
  agent.group.userData.hit = 'agent';
  const tie = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.2, 0.015), smooth(C.gold, { metalness: 0.4 }));
  tie.position.set(0, 0.72, 0.125);
  agent.group.add(tie);
  const badge = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.035, 0.012), smooth(C.gold, { metalness: 0.8, roughness: 0.3 }));
  badge.position.set(-0.07, 0.78, 0.125);
  agent.group.add(badge);
  const clipboard = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.22, 0.015), smooth(0xfffdf9));
  clipboard.position.set(0, -0.32, 0.1);
  clipboard.rotation.x = -0.5;
  agent.armL.add(clipboard);
  agent.armL.rotation.x = -0.95;
  island.add(agent.group);

  const buyerA = makePerson({
    jacket: 0xc65840, shirt: 0xc65840, pants: 0x3d5570,
    hair: 0x1a1a1a, hairstyle: 'bun', skin: 0xc98d64, shoes: 0xf3efe4
  });
  buyerA.group.position.set(-0.5, 0, 1.62);
  buyerA.group.rotation.y = 0.5;
  buyerA.group.userData.hit = 'buyerA';
  island.add(buyerA.group);

  const buyerB = makePerson({
    jacket: 0x8a955f, shirt: 0xf3efe4, pants: 0x4a4038,
    hair: 0x4d3319, hairstyle: 'short', skin: 0xd9a06b, shoes: 0x2a2420
  });
  buyerB.group.position.set(-0.95, 0, 1.25);
  buyerB.group.rotation.y = 0.75;
  buyerB.group.userData.hit = 'buyerB';
  island.add(buyerB.group);
  buyerB.armR.rotation.z = -0.35;
  buyerA.armL.rotation.z = 0.35;

  // the seller, welcoming from the porch
  const seller = makePerson({
    jacket: 0xb98a2f, shirt: 0xf3efe4, pants: 0x55604f,
    hair: 0xcfc6b8, hairstyle: 'bun', skin: 0xd9a06b, shoes: 0x4a4038
  });
  seller.group.position.set(0.28, 0.1, 0.42);
  seller.group.rotation.y = 0.15;
  seller.group.scale.setScalar(0.94);
  seller.group.userData.hit = 'seller';
  island.add(seller.group);

  // OPEN HOUSE sign + balloons
  const signGroup = new THREE.Group();
  signGroup.userData.hit = 'sign';
  signGroup.position.set(1.55, 0, 2.35);
  signGroup.rotation.y = 0.45;
  island.add(signGroup);

  const post = cast(new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.038, 1.0, 8), flat(C.trunk)));
  post.position.y = 0.5;
  signGroup.add(post);

  const signTex = canvasTexture('openhouse', 256, 128, (ctx) => {
    ctx.fillStyle = '#17503a';
    ctx.fillRect(0, 0, 256, 128);
    ctx.strokeStyle = '#d9a940';
    ctx.lineWidth = 6;
    ctx.strokeRect(8, 8, 240, 112);
    ctx.fillStyle = '#e9c979';
    ctx.font = 'bold 44px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.fillText('OPEN', 128, 56);
    ctx.fillText('HOUSE', 128, 104);
  });
  const board = cast(new THREE.Mesh(
    new THREE.BoxGeometry(0.8, 0.42, 0.04),
    [flat(0x14432f), flat(0x14432f), flat(0x14432f), flat(0x14432f),
     new THREE.MeshStandardMaterial({ map: signTex, roughness: 0.8 }), flat(0x14432f)]
  ));
  board.position.y = 0.9;
  signGroup.add(board);

  const balloons = [];
  [[0.22, 0xd9a940], [0.4, 0xc65840], [0.31, 0xf6f3ec]].forEach((b, i) => {
    const bg = new THREE.Group();
    const string = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 0.5 + i * 0.14, 4), smooth(0xd6cfbe));
    string.position.y = (0.5 + i * 0.14) / 2;
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.13, 12, 12), smooth(b[1], { roughness: 0.35 }));
    ball.scale.y = 1.15;
    ball.position.y = 0.57 + i * 0.14;
    bg.add(string, ball);
    bg.position.set(b[0] - 0.28, 1.08, 0.05 * i);
    signGroup.add(bg);
    balloons.push(bg);
  });

  // fireflies
  const N = 90;
  const positions = new Float32Array(N * 3);
  const seeds = [];
  for (let i = 0; i < N; i++) {
    const r = 2.6 + Math.random() * 2.6;
    const a = Math.random() * Math.PI * 2;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = -1.5 + Math.random() * 4.5;
    positions[i * 3 + 2] = Math.sin(a) * r;
    seeds.push(Math.random() * Math.PI * 2);
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const flies = new THREE.Points(pGeo, new THREE.PointsMaterial({ color: 0xe9c979, size: 0.055, transparent: true, opacity: 0.85, sizeAttenuation: true }));
  scene.add(flies);

  return {
    scene, island, flies, seeds, houseObj, agent, buyerA, buyerB, seller,
    balloons, signGroup, flagObj, mailboxRefs: { group: mailbox, flagPivot: mbFlagPivot, envelope }, oakRefs: { group: oak, canopy }
  };
}

/* ── component ──────────────────────────────────────────── */

export default function Hero3D({ fallback }) {
  const mountRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    } catch (e) {
      setFailed(true);
      return undefined;
    }

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputEncoding = THREE.sRGBEncoding;
    mount.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 60);
    camera.position.set(0, 2.4, 10.2);
    camera.lookAt(0, 0.5, 0);

    const S = buildScene();
    const { scene, island, flies, seeds } = S;

    const size = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    size();
    window.addEventListener('resize', size);

    /* ── one-shot effect timeline ── */
    let t = 0;
    const fx = {};
    const trigger = (name, dur) => { fx[name] = { start: t, dur }; };
    const prog = name => {
      const f = fx[name];
      if (!f) return null;
      const p = (t - f.start) / f.dur;
      if (p >= 1) { delete fx[name]; return null; }
      return p;
    };
    const easeOut = p => 1 - Math.pow(1 - p, 3);

    /* ── speech bubbles ── */
    const bubbles = [];
    function say(figure, text, dur) {
      // one bubble per figure — replace an active one instead of stacking
      for (let i = bubbles.length - 1; i >= 0; i--) {
        if (bubbles[i].parent === figure.group) {
          figure.group.remove(bubbles[i].sprite);
          bubbles[i].sprite.material.dispose();
          bubbles.splice(i, 1);
        }
      }
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: bubbleTex(text), transparent: true, opacity: 0, depthWrite: false
      }));
      sprite.center.set(0.5, 0);
      sprite.scale.set(2.6, 1.03, 1);
      sprite.position.y = 1.26;
      sprite.renderOrder = 10;
      figure.group.add(sprite);
      bubbles.push({ sprite, parent: figure.group, start: t, dur: dur || 5.5 });
    }

    /* ── particle bursts ── */
    const bursts = [];
    function burst(kind, pos, count) {
      const texMap = { heart: heartTex, dollar: dollarTex, smoke: smokeTex, leaf: leafTex };
      const parts = [];
      const group = new THREE.Group();
      group.position.copy(pos);
      for (let i = 0; i < count; i++) {
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({
          map: texMap[kind](), transparent: true, opacity: 0.95, depthWrite: false
        }));
        const s = kind === 'smoke' ? 0.3 + Math.random() * 0.25 : 0.16 + Math.random() * 0.1;
        sp.scale.set(s, s, 1);
        sp.position.set((Math.random() - 0.5) * 0.25, Math.random() * 0.1, (Math.random() - 0.5) * 0.25);
        group.add(sp);
        parts.push({
          sp,
          vx: (Math.random() - 0.5) * (kind === 'leaf' ? 0.5 : 0.3),
          vy: kind === 'leaf' ? -0.35 - Math.random() * 0.25 : 0.55 + Math.random() * 0.45,
          vz: (Math.random() - 0.5) * 0.25,
          delay: Math.random() * 0.25
        });
      }
      island.add(group);
      bursts.push({ group, parts, start: t, dur: kind === 'smoke' ? 2.4 : 1.8 });
    }

    /* ── click handling ── */
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let mailFlagUp = false;

    function pickAt(clientX, clientY) {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      for (let i = 0; i < hits.length; i++) {
        let obj = hits[i].object;
        while (obj && !obj.userData.hit) obj = obj.parent;
        if (obj) return obj.userData.hit;
      }
      return null;
    }

    const ACTIONS = {
      house() {
        trigger('house', 4.6);
        const p = S.houseObj.chimneyLocal.clone();
        p.applyMatrix4(new THREE.Matrix4().makeTranslation(-0.3, 0, -0.7));
        burst('smoke', p, 7);
        say(S.seller, 'Come on in!', 4.5);
      },
      agent() {
        trigger('agent', 3.2);
        say(S.agent, 'Three beds, two baths — just listed!');
      },
      buyerA() {
        trigger('buyerA', 1.6);
        burst('heart', S.buyerA.group.position.clone().setY(1.35), 6);
        say(S.buyerA, 'This is the one!');
      },
      buyerB() {
        trigger('buyerB', 3.0);
        burst('dollar', S.buyerB.group.position.clone().setY(1.3), 5);
        say(S.buyerB, "Let's make an offer.");
      },
      seller() {
        trigger('seller', 3.0);
        say(S.seller, 'We raised our kids here ♥');
      },
      mailbox() {
        trigger('mailbox', 2.2);
        mailFlagUp = !mailFlagUp;
      },
      sign() {
        trigger('sign', 1.8);
      },
      flag() {
        trigger('flag', 3.2);
      },
      oak() {
        trigger('oak', 1.6);
        burst('leaf', S.oakRefs.group.position.clone().setY(1.1), 7);
      }
    };

    const onClick = e => {
      const hit = pickAt(e.clientX, e.clientY);
      if (hit && ACTIONS[hit]) ACTIONS[hit]();
    };
    renderer.domElement.addEventListener('click', onClick);

    const onHover = e => {
      renderer.domElement.style.cursor = pickAt(e.clientX, e.clientY) ? 'pointer' : 'default';
    };
    renderer.domElement.addEventListener('mousemove', onHover);

    /* ── parallax ── */
    const target = { x: 0, y: 0 };
    const onMove = e => {
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener('mousemove', onMove);

    let raf;
    const posAttr = flies.geometry.attributes.position;
    const baseY = [];
    for (let i = 0; i < seeds.length; i++) baseY.push(posAttr.array[i * 3 + 1]);
    const flagPos = S.flagObj.flag.geometry.attributes.position;

    const renderFrame = () => {
      island.rotation.y = t * 0.1;
      island.position.y = Math.sin(t * 0.7) * 0.12;

      for (let i = 0; i < seeds.length; i++) {
        posAttr.array[i * 3 + 1] = baseY[i] + Math.sin(t * 0.9 + seeds[i]) * 0.25;
      }
      posAttr.needsUpdate = true;
      flies.rotation.y = t * 0.03;

      /* idle motion */
      const pAgent = prog('agent');
      if (pAgent !== null) {
        // excited pitch: both arms up, fast wave
        S.agent.armR.rotation.z = -2.1 + Math.sin(t * 9) * 0.35;
        S.agent.armR.rotation.x = -0.2;
        S.agent.armL.rotation.x = -1.4;
        S.agent.head.rotation.y = Math.sin(t * 5) * 0.15;
      } else {
        S.agent.armR.rotation.z = -1.1 + Math.sin(t * 1.3) * 0.28;
        S.agent.armR.rotation.x = -0.3;
        S.agent.armL.rotation.x = -0.95;
        S.agent.head.rotation.y = Math.sin(t * 0.65) * 0.28;
      }

      const pA = prog('buyerA');
      if (pA !== null) {
        // two joyful hops
        S.buyerA.group.position.y = Math.abs(Math.sin(pA * Math.PI * 2)) * 0.22;
        S.buyerA.armL.rotation.z = 2.6;
        S.buyerA.armR.rotation.z = -2.6;
      } else {
        S.buyerA.group.position.y = 0;
        S.buyerA.armL.rotation.z = 0.35;
        S.buyerA.armR.rotation.z = 0;
        S.buyerA.head.rotation.x = Math.max(0, Math.sin(t * 1.8)) * 0.1;
        S.buyerA.head.rotation.y = -0.3 + Math.sin(t * 0.5) * 0.15;
      }

      const pB = prog('buyerB');
      if (pB !== null) {
        // vigorous agreeing nod + a thoughtful chin stroke
        S.buyerB.head.rotation.x = Math.sin(t * 10) * 0.14;
        S.buyerB.armR.rotation.x = -1.5;
      } else {
        S.buyerB.head.rotation.y = -0.2 + Math.sin(t * 0.42 + 1.5) * 0.2;
        S.buyerB.head.rotation.x = Math.max(0, Math.sin(t * 1.8 + 0.8)) * 0.08;
        S.buyerB.armR.rotation.x = 0;
        S.buyerB.armR.rotation.z = -0.35;
      }

      const pS = prog('seller');
      if (pS !== null) {
        S.seller.armR.rotation.z = -2.4 + Math.sin(t * 8) * 0.5;
        S.seller.armL.rotation.z = 2.4 - Math.sin(t * 8) * 0.5;
      } else {
        S.seller.armR.rotation.z = -0.15 + Math.sin(t * 0.8) * 0.06;
        S.seller.armL.rotation.z = 0.15;
        S.seller.head.rotation.y = Math.sin(t * 0.6 + 2) * 0.2;
      }

      /* house: door + light + windows */
      const pH = prog('house');
      let open = 0;
      if (pH !== null) {
        open = pH < 0.18 ? easeOut(pH / 0.18) : pH > 0.82 ? easeOut((1 - pH) / 0.18) : 1;
      }
      S.houseObj.doorPivot.rotation.y = -1.9 * open;
      S.houseObj.glow.intensity = 0.65 + open * (1.1 + Math.sin(t * 9) * 0.18);
      S.houseObj.winMat.emissiveIntensity = 0.85 + open * 0.9;

      /* mailbox */
      const pM = prog('mailbox');
      const flagTarget = mailFlagUp ? -1.35 : 0;
      S.mailboxRefs.flagPivot.rotation.z += (flagTarget - S.mailboxRefs.flagPivot.rotation.z) * 0.12;
      if (pM !== null) {
        S.mailboxRefs.envelope.visible = true;
        const arc = Math.sin(Math.min(pM * 1.4, 1) * Math.PI);
        S.mailboxRefs.envelope.position.y = 0.62 + arc * 0.55;
        S.mailboxRefs.envelope.position.z = pM * 0.3;
        S.mailboxRefs.envelope.rotation.z = pM * 2.2;
        S.mailboxRefs.envelope.rotation.x = pM * 1.1;
      } else {
        S.mailboxRefs.envelope.visible = false;
      }

      /* sign wobble + balloon excitement */
      const pSign = prog('sign');
      const wob = pSign !== null ? Math.sin(pSign * Math.PI * 4) * (1 - pSign) * 0.16 : 0;
      S.signGroup.rotation.z = wob;
      const excite = pSign !== null ? 1 + (1 - pSign) * 2.2 : 1;
      S.balloons.forEach((b, i) => {
        b.rotation.z = Math.sin(t * 1.1 * excite + i * 1.7) * 0.14 * excite;
        b.rotation.x = Math.cos(t * 0.9 * excite + i * 1.3) * 0.1 * excite;
      });

      /* oak shake */
      const pOak = prog('oak');
      S.oakRefs.canopy.rotation.z = pOak !== null ? Math.sin(t * 26) * 0.07 * (1 - pOak) : 0;

      /* flag ripple (+ gust) */
      const pFlag = prog('flag');
      const gust = pFlag !== null ? 1 + (1 - pFlag) * 2.4 : 1;
      for (let i = 0; i < flagPos.count; i++) {
        const x = S.flagObj.base[i * 3];
        const y = S.flagObj.base[i * 3 + 1];
        const d = (x + 0.26) / 0.52;
        flagPos.array[i * 3 + 2] =
          Math.sin(x * 14 + t * 4.5 * gust) * 0.035 * d * gust +
          Math.sin(y * 8 + t * 3 * gust) * 0.012 * d;
      }
      flagPos.needsUpdate = true;
      S.flagObj.flag.geometry.computeVertexNormals();

      /* bubbles */
      for (let i = bubbles.length - 1; i >= 0; i--) {
        const b = bubbles[i];
        const p = (t - b.start) / b.dur;
        if (p >= 1) {
          b.parent.remove(b.sprite);
          b.sprite.material.dispose();
          bubbles.splice(i, 1);
          continue;
        }
        b.sprite.material.opacity = p < 0.12 ? p / 0.12 : p > 0.8 ? (1 - p) / 0.2 : 1;
        b.sprite.position.y = 1.28 + p * 0.06;
      }

      /* bursts */
      for (let i = bursts.length - 1; i >= 0; i--) {
        const bu = bursts[i];
        const p = (t - bu.start) / bu.dur;
        if (p >= 1) {
          island.remove(bu.group);
          bu.parts.forEach(pt => pt.sp.material.dispose());
          bursts.splice(i, 1);
          continue;
        }
        bu.parts.forEach(pt => {
          const lp = Math.max(0, p - pt.delay);
          pt.sp.position.x += pt.vx * 0.016;
          pt.sp.position.y += pt.vy * 0.016;
          pt.sp.position.z += pt.vz * 0.016;
          pt.sp.material.opacity = Math.max(0, 0.95 * (1 - lp * 1.15));
        });
      }

      camera.position.x += (target.x * 1.1 - camera.position.x) * 0.04;
      camera.position.y += (2.4 - target.y * 0.7 - camera.position.y) * 0.04;
      camera.lookAt(0, 0.5, 0);

      renderer.render(scene, camera);
    };

    const loop = () => {
      t += 0.016;
      renderFrame();
      raf = requestAnimationFrame(loop);
    };

    if (reduced) {
      renderFrame();
      // still allow clicks to render a response frame-by-frame
      const stepOnClick = () => { t += 0.016; renderFrame(); };
      renderer.domElement.addEventListener('click', stepOnClick);
    } else {
      loop();
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      window.removeEventListener('mousemove', onMove);
      renderer.domElement.removeEventListener('click', onClick);
      renderer.domElement.removeEventListener('mousemove', onHover);
      scene.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          (Array.isArray(obj.material) ? obj.material : [obj.material]).forEach(m => m.dispose());
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  if (failed) return fallback || null;

  return (
    <div className="ld-3d" aria-hidden="true">
      <div className="ld-3d-canvas" ref={mountRef} />
      <div className="ld-3d-hint">Psst — try clicking around the open house</div>
      <div className="ld-3d-chip ld-3d-chip-a">
        <span className="ld-3d-chip-dot" /> Showing accepted · 412 Maple Crest Dr
        <b>$45</b>
      </div>
    </div>
  );
}
